/**
 * Helpers for working with phone numbers that store the country dialing code
 * inline, in the format: `+<countryNumberCode>-<number>`.
 *
 * Examples of valid stored values:
 *   +1-1234567890
 *   +1242-1234567890
 *   +229-1234567890
 *   +34-1234567890
 */

export const DEFAULT_PHONE_COUNTRY_CODE = '1'; // United States

export interface SplitPhone {
  /** Country dialing code without the leading '+', e.g. "1", "91", "1242". */
  countryCode: string;
  /** Local phone number (digits only), e.g. "1234567890". */
  number: string;
}

/**
 * Splits a stored phone string into its country code and local number parts.
 * Accepts values with or without the leading '+' and with or without a '-'
 * separator. When no country code is present, the provided fallback is used.
 */
export function splitPhone(
  phone: string | null | undefined,
  fallbackCountryCode: string = DEFAULT_PHONE_COUNTRY_CODE
): SplitPhone {
  const raw = (phone ?? '').trim();
  if (!raw) {
    return { countryCode: fallbackCountryCode, number: '' };
  }

  // Expected format: +<code>-<number>
  const match = raw.match(/^\+?(\d+)-(\d*)$/);
  if (match) {
    return { countryCode: match[1], number: match[2] };
  }

  // Fallback: no separator. Treat the whole thing as the local number so we
  // never lose digits for legacy records that were saved without a code.
  const digitsOnly = raw.replace(/[^\d]/g, '');
  return { countryCode: fallbackCountryCode, number: digitsOnly };
}

/**
 * Combines a country code and local number into the stored phone format.
 * Returns an empty string when there is no local number.
 */
export function combinePhone(
  countryCode: string | null | undefined,
  numberValue: string | null | undefined
): string {
  const code = (countryCode ?? '').toString().replace(/[^\d]/g, '');
  const num = (numberValue ?? '').toString().trim();
  if (!num) {
    return '';
  }
  const finalCode = code || DEFAULT_PHONE_COUNTRY_CODE;
  return `+${finalCode}-${num}`;
}
